import './styles.scss'
import React, { memo, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { DragIcon } from 'Assets/Icons'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { useIsMobileView600 } from 'Utils/commonMethods'
import { getLocalChartPreference, getLocalForexPreference, getLocalUserDetails, setLocalWithChartPreference, setLocalWithForexPreference } from 'Utils/LocalStorage_Handler'
import ComingSoonModal from 'Components/UIComponent/ComingSoonModal'
import ErrorMessageModal from 'Components/UIComponent/ConfirmationModal'
import useAnalytics from 'Utils/useAnalytics'
import { USER_EVENTS } from 'Utils/Constants/analyticsEvents'
import { ROUTE_STRINGS } from 'Utils/Constants'
import ForexChart from '../ForexChart'
import AddCurrencyModal from './AddCurrency'

/* eslint-disable react/display-name */
const Forex = memo(({ forexDataLoader, setForexDataLoader, mockLiveCurrencyData, mockForexData, showError, showConfirmation }) => {
  const { t } = useTranslation()
  const history = useHistory()

  let currencyOrder = []
  let currencyTickerOrder = []
  const rowRefs = useRef({})
  const { isWebView } = useIsMobileView600()
  const { trackEvent } = useAnalytics()

  const appChartPref = {
    ticker: '',
    chartTypePref: '',
    chartTheme: '',
    gridPref: ''
  }

  const chartPreferenceRef = useRef(getLocalChartPreference() || appChartPref)

  const userDetails = getLocalUserDetails()
  const [liveCurrencyData, setLiveCurrencyData] = useState([])
  const [draggedRowIndex, setDraggedRowIndex] = useState(null)
  const [chartLoader, setChartLoader] = useState(false)
  const [isApiError, setIsApiError] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState(null) // Handles selected chart in user forex list
  const [selectedTicker, setSelectedTicker] = useState(null) // Handles selected chart in forex chart dropdown
  const [forexLatestData, setForexLatestData] = useState(null) // Handles forex Latest data for chart
  const [isBottomPanelVisible, setIsBottomPanelVisible] = useState(false)
  const [addCurrencyLoader, setAddCurrencyLoader] = useState(false)
  const [showModal, setShowModal] = useState(false)

  // const [highlightedCurrencies, setHighlightedCurrencies] = useState([]) // Commented for Future Ref
  // const [chartData, setChartData] = useState({})
  // const [chartOptions, setChartOptions] = useState({})
  // const forexRef = useRef(null)

  useEffect(() => {
    // this if condition is for testing purpose
    if (mockForexData) {
      setLiveCurrencyData(mockLiveCurrencyData)
    }

    fetchForexData()
    const cleanUpWebSocket = connectToWebSocket()

    return () => {
      cleanUpWebSocket()
    }
  }, [])

  useEffect(() => {
    if (selectedCurrency && rowRefs?.current[selectedCurrency] && userDetails) {
      const row = rowRefs.current[selectedCurrency]
      const container = document.querySelector('.forex-table-container')

      if (container && row) {
        const rowTop = row.offsetTop
        const rowHeight = row.offsetHeight
        const containerHeight = container.offsetHeight

        const scrollPosition = rowTop - container.offsetTop - containerHeight / 2 + rowHeight / 2

        container.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        })
      }
    }
  }, [selectedCurrency])

  const fetchForexData = async () => {
    setAddCurrencyLoader(true)
    setIsApiError(false)

    const apiUrl = userDetails ? apiAdapterCoffeeWeb.getForexDataForUser({ targetCountryIso3: userDetails?.userCountryDetails?.iso3, userId: userDetails?.id }) : apiAdapterCoffeeWeb.getForexDataForApp()

    try {
      const { data: forexResponseData } = await apiUrl

      const filteredForexResponse = forexResponseData.returnLst.filter((el) => el?.targetCountryIso3)

      const isSelectedChartInForexList = filteredForexResponse.find((el) => el?.symbolTicker5 === getLocalChartPreference()?.ticker) // Find if ticket in LocalStorage is in User List or not

      if (!isSelectedChartInForexList && userDetails) {
        // If user is logged-in and chart opened is not in user list, fallback to 0th index
        chartPreferenceRef.current.ticker = filteredForexResponse[0]?.symbolTicker5
        chartPreferenceRef.current.chartTypePref = getLocalChartPreference()?.chartTypePref
        chartPreferenceRef.current.chartTheme = getLocalChartPreference()?.chartTheme
        chartPreferenceRef.current.gridPref = getLocalChartPreference()?.gridPref

        setSelectedCurrency(filteredForexResponse[0]?.targetCurrency)
        setSelectedTicker(filteredForexResponse[0]?.symbolTicker5)
        setLocalWithChartPreference(chartPreferenceRef.current)
      }

      let countryCurrencyData = []

      if (filteredForexResponse?.length > 0) {
        countryCurrencyData = filteredForexResponse
      } else {
        countryCurrencyData = []
      }

      setLiveCurrencyData(countryCurrencyData)

      if (filteredForexResponse?.length === 0) {
        setIsApiError(true)
      }
    } catch (error) {
      console.log(error)
      setIsApiError(true)
    } finally {
      setForexDataLoader(false)
      setAddCurrencyLoader(false)
    }
  }

  const connectToWebSocket = () => {
    const socketUrl = `${process.env.REACT_APP_BASE_URL_FOREX_WSS}/ws/forex`

    const socket = new WebSocket(socketUrl)

    socket.onmessage = (event) => {
      const updatedDataList = JSON.parse(event?.data)?.returnLst // Parse and store socket message

      setForexLatestData({ ...updatedDataList[0], todayPrice: true }) // Add a flag for today's data to handle text in tooltip between last and close

      setLiveCurrencyData((prevData) => {
        const updateMap = new Map()

        // To store values from socket in the set for improving access time
        updatedDataList.forEach((update) => {
          if (update?.targetCurrency) {
            updateMap.set(update.targetCurrency, update)
          }
        })

        let hasAnyUpdate = false
        // const highlights = [] // Commented for Future Ref

        // Loop over user forex list
        const updatedList = prevData.map((item) => {
          const update = updateMap.get(item.targetCurrency) // Get value stored with socket data by target currency of user Forex List

          if (update) {
            const hasChanged = Number(update.tradePrice8) !== Number(item.tradePrice8) || Number(update.pctChg362) !== Number(item.pctChg362) // Check if prev and current value is different

            if (hasChanged) {
              // highlights.push(update.targetCurrency) // Commented for Future Ref
              hasAnyUpdate = true

              return {
                ...item,
                tradePrice8: update.tradePrice8,
                pctChg362: update.pctChg362,
                symbolTicker5: update.symbolTicker5 || item.symbolTicker5
              }
            }
          }

          return item // return original if no update or no changes
        })

        //  Commented for Future ref

        // if (hasAnyUpdate && highlights.length > 0) {
        //   setHighlightedCurrencies(highlights)
        //   setTimeout(() => {
        //     setHighlightedCurrencies((prev) => prev.filter((cur) => !highlights.includes(cur)))
        //   }, 2000)
        // }

        return hasAnyUpdate ? updatedList : prevData
      })
    }

    function closeConnection() {
      socket.close()
    }

    return closeConnection
  }

  const handleDragStart = (event, index) => {
    event.dataTransfer.setData('text/plain', index)

    setDraggedRowIndex(index)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDrop = async (event, index) => {
    event.preventDefault()

    if (draggedRowIndex === null || draggedRowIndex === index) return

    const updatedRows = [...liveCurrencyData]
    const draggedItem = updatedRows[draggedRowIndex]

    updatedRows.splice(draggedRowIndex, 1)
    updatedRows.splice(index, 0, draggedItem)

    currencyOrder = updatedRows?.map((el) => el?.targetCurrency)
    currencyTickerOrder = updatedRows?.reduce((acc, el) => {
      if (el?.symbolTicker5) acc.push(el.symbolTicker5)

      return acc
    }, [])

    const forexReorderedPayload = {
      id: 0,
      userid: userDetails?.id,
      symbolTickerOrder: currencyTickerOrder
    }

    try {
      await apiAdapterCoffeeWeb.updateUserForexPreference(forexReorderedPayload)
      setLiveCurrencyData(updatedRows)
    } catch {
      showError({
        title: t('ERROR_TITLE'),
        message: `<p>${t('SOMETHING_WENT_WRONG')}</p><p>${t('TRY_AGAIN_MESSAGE')}</p>`,
        buttonLabel: t('OK')
      })
    }

    setDraggedRowIndex(null)
  }

  const dataColor = (percentageChange) => {
    if (percentageChange === 0) return ''

    return percentageChange > 0 ? 'bold_and_green' : 'bold_and_red'
  }

  const handleAddCurrencyAction = () => {
    userDetails ? setShowModal(true) : history.push(ROUTE_STRINGS.login)
  }

  // Delete Action commented for future Req.
  // const triggerDeleteConfirmation = (event, symbolTicker5, targetCurrency) => {
  //   event.stopPropagation()

  //   showConfirmation({
  //     title: t('DELETE_FOREX_CURRENCY_FOR_USER'),
  //     message: `<p>${t('DELETE_FOREX_CURRENCY_CONFIRMATION')}</p>`,
  //     buttonLabel: t('DELETE'),
  //     dismissButtonLabel: t('CANCEL'),
  //     buttonAction: 'danger-action',
  //     onConfirm: () => {
  //       handleCurrencyDeleteAction(symbolTicker5, targetCurrency)
  //     }
  //   })
  // }

  // const handleCurrencyDeleteAction = async (symbolTicker5, targetCurrency) => {
  //   try {
  //     const deleteApiResponse = await apiAdapterCoffeeWeb.deleteUserForexCurrency({ userId: userDetails.id, symbolTicker: symbolTicker5 })
  //     let updatedLiveCurrencyData = liveCurrencyData

  //     if (deleteApiResponse.data) {
  //       updatedLiveCurrencyData = liveCurrencyData.filter((el) => el.symbolTicker5 !== symbolTicker5)
  //       if (selectedCurrency === targetCurrency && userDetails) {
  //         setSelectedCurrency(updatedLiveCurrencyData[0].targetCurrency)
  //         setSelectedTicker(updatedLiveCurrencyData[0].symbolTicker5)
  //         chartPreferenceRef.current.ticker = updatedLiveCurrencyData[0].symbolTicker5

  //         setLocalWithChartPreference(chartPreferenceRef.current)
  //       }
  //     } else {
  //       showError({
  //         title: t('ERROR_TITLE'),
  //         message: `<p>${t('DELETE_USER_FOREX_CURRENCY_FAIL')}</p><p>${t('TRY_AGAIN_MESSAGE')}</p>`,
  //         buttonLabel: t('OK')
  //       })
  //     }

  //     setLiveCurrencyData(updatedLiveCurrencyData)
  //   } catch (error) {
  //     showError({
  //       title: t('ERROR_TITLE'),
  //       message: `<p>${t('DELETE_USER_FOREX_CURRENCY_FAIL')}</p><p>${t('TRY_AGAIN_MESSAGE')}</p>`,
  //       buttonLabel: t('OK')
  //     })
  //   }
  // }

  const handleRowClick = (currencyData) => {
    setSelectedCurrency(currencyData?.targetCurrency)
    setSelectedTicker(currencyData?.symbolTicker5)

    chartPreferenceRef.current.ticker = currencyData?.symbolTicker5
    chartPreferenceRef.current.chartTypePref = getLocalChartPreference()?.chartTypePref
    chartPreferenceRef.current.chartTheme = getLocalChartPreference()?.chartTheme
    chartPreferenceRef.current.gridPref = getLocalChartPreference()?.gridPref

    trackEvent(USER_EVENTS.FOREX_CURRENCY_TAP) // user-analytics
    if (userDetails) {
      setLocalWithChartPreference(chartPreferenceRef.current)
    }
  }

  const formatDollarIndex = (currency = '') => {
    if (!currency || typeof currency !== 'string') return ''

    const [first, ...rest] = currency.trim().split(/\s+/)
    const formattedFirst = first === '$' ? 'DOLLAR' : first

    return [formattedFirst, ...rest].join(' ')
  }

  return (
    <div>
      {(forexDataLoader || addCurrencyLoader) && !isApiError && (
        <div className="forex-loader-container" data-testid="forex-loader-container">
          <ProgressSpinner className="progress-spinner" strokeWidth="4" animationDuration="2s" />
          <p data-testid="loading-text">Loading latest Forex data.</p>
        </div>
      )}{' '}
      {isApiError && !forexDataLoader && (
        <div className="forex-error-message-div" data-testid="forex-error-message-div">
          <i className="pi pi-times-circle" data-testid="forex-error-icon"></i>
          <div className="forex-error-message-text" data-testid="forex-error-message-text">
            Unable to load data. Please try again later.
          </div>
        </div>
      )}
      {!forexDataLoader && !isApiError && !addCurrencyLoader && (
        <div className="forex-component-wrapper" data-testid="forex-component-wrapper">
          <div className={`currency-container ${!userDetails ? 'guest' : ''}`} data-testid="currency-container">
            {liveCurrencyData?.length > 0 && (
              <>
                <div className="base-currency" data-testid="base-currency">
                  <div className="center-content">
                    <span data-testid="rate-usd-text">RATE / USD</span>
                    <img src={`https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${'USA'}.svg`} alt="Flag" data-testid="target-country-flag" />
                  </div>
                  <div className="right-content">
                    <Button label={t('MANAGE_CURRENCY')} icon="pi pi-plus" onClick={handleAddCurrencyAction} className={`add-currency-button ${userDetails?.id ? '' : 'add-currency-non-clickable'}`} />
                  </div>
                </div>
              </>
            )}
            <div className={`forex-table-container ${forexDataLoader ? 'forex-loading' : ''} ${!userDetails ? 'no-user-details' : ''}`} data-testid="forex-table-container">
              <table data-testid="forex-component-table">
                <tbody>
                  {liveCurrencyData?.map(({ countryName, targetCurrency, tradePrice8, targetCountryIso3, pctChg362, symbolTicker5, targetCountryName }, index) => {
                    const isFirstRow = index === 0

                    // Highlighting Logic
                    // const isHighlighted = highlightedCurrencies.includes(targetCurrency)
                    // <tr draggable onDragStart={() => handleDragStart(index)} onDragOver={handleDragOver} onDrop={() => handleDrop(index)} className={`each-currency ${isHighlighted ? 'highlight' : ''}`} key={index} data-testid="currency-table-row">
                    return (
                      <tr
                        ref={(item) => {
                          rowRefs.current[targetCurrency] = item
                        }}
                        draggable={userDetails && !isFirstRow}
                        onClick={() => {
                          handleRowClick({ targetCurrency, symbolTicker5 })

                          setIsBottomPanelVisible(true)
                        }}
                        onDragStart={(event) => {
                          if (userDetails && !isFirstRow) handleDragStart(event, index)
                        }}
                        onDragOver={(event) => {
                          if (userDetails && !isFirstRow) event.preventDefault()
                        }}
                        onDrop={(event) => {
                          if (userDetails && !isFirstRow) handleDrop(event, index)
                        }}
                        className={`each-currency ${isFirstRow ? 'sticky-row' : ''} ${selectedCurrency === targetCurrency ? 'highlighted' : ''}`}
                        key={index}
                        data-testid="currency-table-row"
                      >
                        {/* <td className="target-currency-column" data-testid="country-name">
                          <div className="country-type" data-testid="country-type">
                            <img src={`https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${targetCountryIso3}.svg`} alt="Flag" data-testid="country-flag-image" />
                            {targetCurrency}
                          </div>
                        </td> */}
                        <td className="target-currency-column" data-testid="country-name">
                          <div className="country-type" data-testid="country-type">
                            <img src={`https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${targetCountryIso3}.svg`} alt="Flag" data-testid="country-flag-image" />

                            <div className="currency-text-wrapper">
                              <span className="currency-code">{window.innerWidth > 1024 ? formatDollarIndex(targetCurrency) : targetCurrency}</span> {isWebView && targetCountryName && <span className="country-name">- {targetCountryName && targetCountryName?.length > 15 ? `${targetCountryName?.substring(0, 12)?.toUpperCase()}...` : targetCountryName?.toUpperCase()}</span>}
                            </div>
                          </div>
                        </td>

                        <td data-testid="last-change" className="last-change-column">
                          <div className={`trade-price ${dataColor(Number(pctChg362))} ${userDetails ? '' : 'guest'}`} data-testid="trade-price">
                            {tradePrice8}
                          </div>
                        </td>
                        <td data-testid="percentage-value" className="percentage-change-column">
                          <div className={`percentage-value ${dataColor(Number(pctChg362))} ${userDetails ? '' : 'guest'}`} data-testid="percentage-change-div">
                            <div className="percent-change" data-testid="percentage-change">
                              <span data-testid="percentage-change-362">{Number(pctChg362) >= 0 ? '+' : '-'}</span>
                              <span>{Math.abs(Number(pctChg362)).toFixed(2)}%</span>
                            </div>
                            <div>
                              {Number(pctChg362) > 0 && (
                                <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                  <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                  <g id="SVGRepo_iconCarrier">
                                    <path d="M1.41421 16.4322L0 15.018L7.07107 7.94693L13.435 14.3109L17.6777 10.0682L15.9353 8.32587L22.6274 6.53271L20.8343 13.2248L19.0919 11.4825L13.435 17.1393L7.07107 10.7754L1.41421 16.4322Z" fill="#4caf50"></path>{' '}
                                  </g>
                                </svg>
                              )}
                              {Number(pctChg362) < 0 && (
                                <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                  <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                  <g id="SVGRepo_iconCarrier">
                                    <path d="M1.85123 8.10628L0.437012 9.52049L7.50808 16.5916L13.872 10.2276L18.1147 14.4702L16.3723 16.2126L23.0644 18.0058L21.2713 11.3136L19.5289 13.056L13.872 7.39917L7.50808 13.7631L1.85123 8.10628Z" fill="#ff1100"></path>{' '}
                                  </g>
                                </svg>
                              )}
                              {Number(pctChg362) === 0 && <div>{/* This has been left empty intentionally */}</div>}
                            </div>
                          </div>
                        </td>
                        {/* <td className="chart-icon-td">{selectedCurrency === targetCurrency && isWebView && <i className="pi pi-chart-line chart-icon"></i>}</td> */}
                        <td className="input-td">{selectedCurrency === targetCurrency && isWebView && <input type="radio" name="currencySelector" checked={true} readOnly />}</td>

                        {/* Commented if Delete is required  */}
                        {/* <td>{!isFirstRow && <i className={`pi pi-trash ${userDetails?.userCountryDetails?.currency?.toLowerCase() === targetCurrency?.toLowerCase() ? 'disabled-icon' : ''}`} onClick={(e) => triggerDeleteConfirmation(e, symbolTicker5, targetCurrency)}></i>}</td> */}
                        <td className="drag-icon-td" data-testid="drag-icon-td">
                          <div className="icon-container" data-testid="icon-container">
                            {!isFirstRow && <DragIcon data-testid="drag-icon" />}{' '}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className={`graph-contaner ${forexDataLoader ? 'forex-loading' : ''}`} data-testid="graph-container">
            <ForexChart setIsBottomPanelVisible={setIsBottomPanelVisible} isBottomPanelVisible={isBottomPanelVisible} countryData={liveCurrencyData} setChartLoader={setChartLoader} chartLoader={chartLoader} forexLatestData={forexLatestData} selectedTickerData={selectedTicker} setSelectedCurrency={setSelectedCurrency} selectedCurrency={selectedCurrency} formatDollarIndex={formatDollarIndex} />
          </div>
        </div>
      )}
      {userDetails && showModal && <AddCurrencyModal userForexSelection={liveCurrencyData} showModal={showModal} setShowModal={setShowModal} fetchForexData={fetchForexData} forexDataLength={liveCurrencyData?.length} />}
    </div>
  )
})

export default ErrorMessageModal(Forex)
